import { useCallback, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type TableOptions,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Ellipsis,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { recordTable } from "@/components/tables/table-presentation";
import { cn } from "@/utils/cn";

interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  totalCount?: number;
  serverSide?: boolean;
  manualPagination?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  rowsPerPageOptions?: number[];
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  getRowClassName?: (row: TData) => string | undefined;
}

function DataTable<TData>({
  columns,
  data,
  totalCount,
  serverSide = false,
  manualPagination = false,
  loading = false,
  emptyMessage,
  rowsPerPageOptions = [10, 25, 50],
  sorting: controlledSorting,
  onSortingChange,
  pagination: controlledPagination,
  onPaginationChange,
  getRowClassName,
}: DataTableProps<TData>) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const resolvedEmptyMessage = emptyMessage ?? t("usage.noRecords");
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize: rowsPerPageOptions[0] || 10,
    },
  );

  const sorting = controlledSorting ?? internalSorting;
  const pagination = controlledPagination ?? internalPagination;
  const usesManualPagination = serverSide || manualPagination;

  const handleSortingChange: TableOptions<TData>["onSortingChange"] = (
    updater,
  ) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    if (onSortingChange) onSortingChange(next);
    else setInternalSorting(next);
  };

  const handlePaginationChange: TableOptions<TData>["onPaginationChange"] = (
    updater,
  ) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    if (onPaginationChange) onPaginationChange(next);
    else setInternalPagination(next);
  };

  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination },
    onSortingChange: handleSortingChange,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: serverSide,
    manualPagination: usesManualPagination,
    pageCount: usesManualPagination
      ? totalCount === undefined
        ? -1
        : Math.ceil(totalCount / pagination.pageSize)
      : undefined,
    ...(!serverSide && { getSortedRowModel: getSortedRowModel() }),
    ...(!usesManualPagination && {
      getPaginationRowModel: getPaginationRowModel(),
    }),
  });

  const totalPages = table.getPageCount();
  const currentPage = pagination.pageIndex;

  const getPageNumbers = useCallback((): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i += 1) pages.push(i);
    } else {
      pages.push(0);
      if (currentPage > 2) pages.push("...");
      for (
        let i = Math.max(1, currentPage - 1);
        i <= Math.min(totalPages - 2, currentPage + 1);
        i += 1
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 3) pages.push("...");
      pages.push(totalPages - 1);
    }
    return pages;
  }, [totalPages, currentPage]);

  return (
    <div>
      <div className={recordTable.container}>
        <table className={cn(recordTable.table, "min-w-[700px]")}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={recordTable.header}
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={recordTable.headerCell}
                    style={{ minWidth: header.column.columnDef.minSize }}
                    scope="col"
                    aria-sort={
                      header.column.getCanSort()
                        ? header.column.getIsSorted() === "asc"
                          ? "ascending"
                          : header.column.getIsSorted() === "desc"
                            ? "descending"
                            : "none"
                        : undefined
                    }
                  >
                    {header.column.getCanSort() ? (
                      <button
                        type="button"
                        className="flex h-full w-full cursor-pointer items-center gap-2 rounded-sm text-start outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        <span className="text-muted-foreground">
                          {{
                            asc: (
                              <ChevronUp aria-hidden="true" className="size-4" />
                            ),
                            desc: (
                              <ChevronDown aria-hidden="true" className="size-4" />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <ChevronsUpDown
                              aria-hidden="true"
                              className="size-4"
                            />
                          )}
                        </span>
                      </button>
                    ) : (
                      <div className="flex h-full w-full items-center">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: pagination.pageSize }).map((_, index) => (
                <tr
                  key={`skeleton-${index}`}
                  className={recordTable.row}
                >
                  {columns.map((_, columnIndex) => (
                    <td key={columnIndex} className={recordTable.cell}>
                      <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
                    </td>
                  ))}
                </tr>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={cn(recordTable.cell, "py-12 text-center text-muted-foreground")}
                >
                  {resolvedEmptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    recordTable.row,
                    getRowClassName?.(row.original),
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={recordTable.cell}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("common.rowsPerPage")}
          </span>
          <Select
            value={String(pagination.pageSize)}
            onValueChange={(value) => table.setPageSize(Number(value))}
            disabled={loading}
            dir={isRTL ? "rtl" : "ltr"}
          >
            <SelectTrigger
              className="h-9 w-20 bg-input shadow-xs"
              aria-label={t("common.rowsPerPage")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {rowsPerPageOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav
          aria-label="Pagination"
          className="flex items-center gap-1 self-end sm:self-auto"
        >
          <Button
            type="button"
            variant="ghost"
            className="h-9 px-2 sm:px-3"
            aria-label={t("common.previous")}
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            {isRTL ? (
              <ChevronRight aria-hidden="true" className="size-4" />
            ) : (
              <ChevronLeft aria-hidden="true" className="size-4" />
            )}
            <span className="hidden sm:inline">{t("common.previous")}</span>
          </Button>

          {getPageNumbers().map((page, index) =>
            page === "..." ? (
              <span
                key={`dots-${index}`}
                className="flex size-9 items-center justify-center text-muted-foreground"
              >
                <Ellipsis aria-hidden="true" className="size-4" />
                <span className="sr-only">More pages</span>
              </span>
            ) : (
              <Button
                type="button"
                key={page}
                onClick={() => table.setPageIndex(page as number)}
                variant={currentPage === page ? "outline" : "ghost"}
                size="icon"
                aria-label={`Page ${(page as number) + 1}`}
                aria-current={currentPage === page ? "page" : undefined}
                className={cn(
                  "size-9",
                  currentPage === page && "bg-accent",
                )}
              >
                {(page as number) + 1}
              </Button>
            ),
          )}

          <Button
            type="button"
            variant="ghost"
            className="h-9 px-2 sm:px-3"
            aria-label={t("common.next")}
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <span className="hidden sm:inline">{t("common.next")}</span>
            {isRTL ? (
              <ChevronLeft aria-hidden="true" className="size-4" />
            ) : (
              <ChevronRight aria-hidden="true" className="size-4" />
            )}
          </Button>
        </nav>
      </div>
    </div>
  );
}

export default DataTable;
export type { DataTableProps };
